import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { isoDay, openingWeek } from '@/lib/meetingWeek';
import { dbApi, dbInsert, dbReorder, dbUpdate, mockList } from '@/lib/mock/db';
import { useWeeks } from '@/lib/queries';
import { queryKeys } from '@/lib/queryKeys';
import type { HqTaskItem, SiteTaskItem, Week, WeeklyExecutiveNote } from '@/lib/types';
import { useStoredState } from '@/lib/utils/useStoredState';

/**
 * 주간회의 데이터 접근.
 *
 * v1은 화면마다 "지금 주차 + 직전 주차"만 골라 읽는 쿼리를 손으로 짰고, 주차를 바꿀 때마다
 * 다시 읽으면서 응답이 뒤바뀌는 경합을 `fetchSeqRef` 카운터로 막았다. 여기서는 전체를 한 번 읽고
 * 화면이 메모리에서 걸러 쓴다 — 사내 규모(주차 수십 개 × 현장 수십 개)에서 그 편이 단순하고,
 * 경합 방지는 TanStack Query 가 이미 한다.
 *
 * 백엔드가 붙어 데이터가 커지면 `queryFn` 에 주차 범위를 넘기고 쿼리 키에 주차 id 를 더하면 된다.
 */

/**
 * 주간 업무 보고서(PDF) 주소 — <b>보고 있는 탭과 주차 그대로</b>다.
 *
 * <p>서버가 굽는다({@code WeeklyReportExportService}). 브라우저 인쇄의 「PDF로 저장」은 사람마다
 * 여백·배율·머리글이 달라져 같은 주차인데 서로 다른 문서가 나오는데, 이 문서는 <b>보증보험 심사</b>에
 * 나가므로 누가 눌러도 같아야 한다.
 *
 * <p>`apiFetch` 가 아니라 링크(`<a href>`)로 여는 것은 세션 쿠키가 그대로 실리고 파일 이름을
 * 서버에서 받기 때문이다(공무 내보내기와 같은 규약).
 */
export function weeklyReportUrl(
  weekId: string,
  scope: 'hq' | 'site',
  options: { includeNotes: boolean; includeList: boolean },
): string {
  const query = new URLSearchParams({ weekId, scope });
  // 넣는 쪽이 기본이라 뺄 때만 적는다 — 주소가 짧아지고, 무엇을 「일부러 뺐는지」가 주소에 남는다.
  if (!options.includeNotes) query.set('notes', 'false');
  if (!options.includeList) query.set('list', 'false');
  return `/api/v1/weekly-report/export.pdf?${query.toString()}`;
}

export function useHqTaskItems() {
  return useQuery({ queryKey: queryKeys.hqTaskItems, queryFn: () => mockList('hqTaskItems') });
}

export function useSiteTaskItems() {
  return useQuery({ queryKey: queryKeys.siteTaskItems, queryFn: () => mockList('siteTaskItems') });
}

export function useWeeklyExecutiveNotes() {
  return useQuery({ queryKey: queryKeys.weeklyExecutiveNotes, queryFn: () => mockList('weeklyExecutiveNotes') });
}

export function useHqTaskMutations() {
  const queryClient = useQueryClient();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: queryKeys.hqTaskItems });

  return {
    add: useMutation({
      mutationFn: (item: Omit<HqTaskItem, 'id'>) => dbInsert('hqTaskItems', item),
      onSuccess: invalidate,
    }),
    // 내용과 일정 날짜를 함께 보낸다. 날짜를 비워 보내면 달력에서 내려간다(서버 `TaskContentRequest`).
    edit: useMutation({
      mutationFn: ({ id, content, scheduleDate }: { id: string; content: string; scheduleDate: string | null }) =>
        dbUpdate('hqTaskItems', id, { content, scheduleDate }),
      onSuccess: invalidate,
    }),
    // 확인은 화면의 confirm 한 번이다 — 비밀번호 재확인(2026-09-03)은 걷었다(사용자 지시 2026-10-28).
    remove: useMutation({
      mutationFn: (id: string) => dbApi(`/v1/hq-tasks/${id}`, 'DELETE'),
      onSuccess: invalidate,
    }),
    /** 목록 전체의 sortOrder 를 1..n 으로 다시 매긴다(서버 /order). */
    reorder: useMutation({
      mutationFn: (ordered: HqTaskItem[]) => dbReorder('hqTaskItems', ordered.map((item) => item.id)),
      onSuccess: invalidate,
    }),
    addMany: useMutation({
      mutationFn: (items: Array<Omit<HqTaskItem, 'id'>>) => dbApi('/v1/hq-tasks/bulk', 'POST', { items }),
      onSuccess: invalidate,
    }),
  };
}

export function useSiteTaskMutations() {
  const queryClient = useQueryClient();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: queryKeys.siteTaskItems });

  return {
    add: useMutation({
      mutationFn: (item: Omit<SiteTaskItem, 'id'>) => dbInsert('siteTaskItems', item),
      onSuccess: invalidate,
    }),
    edit: useMutation({
      mutationFn: ({ id, content, scheduleDate }: { id: string; content: string; scheduleDate: string | null }) =>
        dbUpdate('siteTaskItems', id, { content, scheduleDate }),
      onSuccess: invalidate,
    }),
    /** 본사 업무와 같다 — 확인은 화면의 confirm 한 번이다. */
    remove: useMutation({
      mutationFn: (id: string) => dbApi(`/v1/site-tasks/${id}`, 'DELETE'),
      onSuccess: invalidate,
    }),
    reorder: useMutation({
      mutationFn: (ordered: SiteTaskItem[]) => dbReorder('siteTaskItems', ordered.map((item) => item.id)),
      onSuccess: invalidate,
    }),
    addMany: useMutation({
      mutationFn: (items: Array<Omit<SiteTaskItem, 'id'>>) => dbApi('/v1/site-tasks/bulk', 'POST', { items }),
      onSuccess: invalidate,
    }),
  };
}

/**
 * 대표님 전달사항 저장.
 *
 * 주차 × 종류당 한 건이므로 있으면 고치고 없으면 만든다. 내용을 비우면 지운다 —
 * 빈 문자열을 남기면 대시보드의 "직전 주차 내용 대신 보여주기"가 빈 칸을 찾아낸 것으로 착각한다.
 */
export function useSaveExecutiveNote() {
  const queryClient = useQueryClient();

  return useMutation({
    // 주차×종류당 1건. 전용 업서트 엔드포인트(PUT, id 없음)로 보낸다 — 빈 내용도 그대로 저장하면
    // 대시보드가 "비어 있음"으로 본다(별도 삭제 엔드포인트 없음).
    mutationFn: ({
      weekId,
      noteType,
      content,
    }: {
      existing: WeeklyExecutiveNote | null;
      weekId: string;
      noteType: 'hq' | 'site';
      content: string;
    }) =>
      dbApi('/v1/executive-notes', 'PUT', {
        weekId,
        noteType,
        content: content.trim(),
        updatedAt: new Date().toISOString(),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.weeklyExecutiveNotes }),
  });
}

/**
 * 어느 주차를 보고 있는지. 네 개의 주간회의 화면이 같은 방식으로 정한다.
 *
 * 고른 주차를 기억하되(localStorage), **기억한 값이 아직 유효한지 목록을 받은 뒤에 확인한다** —
 * 그 주차를 지웠거나 다른 기기에서 열었으면 빈 화면이 뜬다.
 *
 * <h2>기억은 <b>그날 하루</b>다 (사용자 결정 2026-09-29)</h2>
 * 종전에는 고른 주차가 브라우저에 영영 남았다. 그러면 {@link openingWeek} 의 기준(화요일부터 다음
 * 주)이 **한 번이라도 주차를 넘겨 본 사람에게는 영영 서지 않는다** — 지난달에 지난주를 한 번 들춰
 * 본 사람은 그 뒤로 매일 그 주차로 열린다.
 *
 * <p>그래서 고른 날짜를 함께 적어 두고, **날이 바뀌면 기준으로 되돌린다**. 기억이 사는 자리는
 * 「지금 이 화면들을 오가는 동안」이지 「이 브라우저의 영구 취향」이 아니다.
 *
 * 주차 목록은 최근 순이므로 다음 항목이 직전 주차다.
 */
export function useWeekSelection(storageKey: string) {
  const { data: weeks = [], isPending } = useWeeks();
  const [stored, setStored] = useStoredState(storageKey);

  const weekId = resolveWeekId(weeks, stored);
  const index = weeks.findIndex((week) => week.id === weekId);

  // 고른 값에 <b>고른 날</b>을 붙여 둔다. 내일 열면 이 값은 낡은 것이 되어 기준이 다시 선다.
  const selectWeek = useCallback(
    (next: string | null) => setStored(next === null ? null : `${next}@${isoDay(new Date())}`),
    [setStored],
  );

  return {
    weeks,
    isPending,
    weekId,
    week: index >= 0 ? (weeks[index] as Week) : null,
    prevWeek: index >= 0 ? weeks[index + 1] ?? null : null,
    selectWeek,
  };
}

function resolveWeekId(weeks: Week[], stored: string | null): string | null {
  const picked = readPick(stored);
  if (picked && weeks.some((week) => week.id === picked)) return picked;

  return openingWeek(weeks)?.id ?? null;
}

/**
 * 기억해 둔 값 → <b>오늘 고른</b> 주차 id. 아니면 `null`.
 *
 * <p>날짜가 붙지 않은 값은 이 규칙 이전에 남은 것이라 낡은 것으로 본다 — 언제 고른 것인지 알 수
 * 없는 값을 오늘 고른 것으로 칠 수는 없다. 지우지는 않는다. 다음에 고르는 순간 덮인다.
 */
function readPick(stored: string | null): string | null {
  if (!stored) return null;
  const at = stored.lastIndexOf('@');
  if (at < 0) return null;
  return stored.slice(at + 1) === isoDay(new Date()) ? stored.slice(0, at) : null;
}
